import React, { useState, useRef, useEffect, useCallback } from "react"
import Button from "@mui/material/Button"
import DeleteIcon from "@mui/icons-material/Delete"
import SendIcon from "@mui/icons-material/Send"
import Alert from "@mui/material/Alert"
import CircularProgress from "@mui/material/CircularProgress"
import Chip from "@mui/material/Chip"
import Box from "@mui/material/Box"
import Typography from "@mui/material/Typography"
import MediaDropZone from "./MediaDropZone"
import MediaUploadCard from "./MediaUploadCard"
import { generateUniqueString } from "utils/media"
import { getRandomInt } from "utils/number"
import { getCMSConfig } from "services/config"
import { verifyResponse } from "utils/httpRequest"
import {
  StyledFileUploadCardZone,
  StyledMediaUploadControlPanel,
} from "./MediaManager.style"

const MediaUpload = ({ onSelect, handleClose }) => {
  const [cmsConfig, setCmsConfig] = useState({})
  const [uploadList, setUploadList] = useState([])
  const [configLoading, setConfigLoading] = useState(true)
  const [configError, setConfigError] = useState("")
  const [selectionError, setSelectionError] = useState("")
  const [stats, setStats] = useState({ total: 0, validated: 0, uploaded: 0, uploading: 0 })
  const uploadListRef = useRef(null)
  uploadListRef.current = uploadList

  const deriveTitleFromFileName = (fileName) => {
    if (!fileName || typeof fileName !== "string") return ""
    const baseName = fileName.split(/[\\/]/).pop() || ""
    const lastDot = baseName.lastIndexOf(".")
    return lastDot > 0 ? baseName.slice(0, lastDot) : baseName
  }

  const onDrop = (acceptedFiles, fileRejections = []) => {
    const rejectionMessages = fileRejections.flatMap(({ file, errors }) =>
      errors.map((error) => `${file.name}: ${error.message}`)
    )
    setSelectionError(rejectionMessages.join(" "))

    const newFiles = acceptedFiles.map((newFile) => ({
      file: newFile,
      key: generateUniqueString(),
      validated: false,
      uploaded: false,
      progress: 0,
      uploading: false,
      duration: 0,
      uploadName: deriveTitleFromFileName(newFile?.name),
      description: ""
    }))
    setUploadList([...uploadList, ...newFiles])
  }

  const initProcess = async () => {
    setConfigLoading(true)
    setConfigError("")
    
    try {
      const response = await getCMSConfig()
      verifyResponse(response)

      const config = {
        S3_PRIVATE_BUCKET: response.data.REACT_APP_S3_PRIVATE_BUCKET,
        S3_PUBLIC_BUCKET: response.data.REACT_APP_S3_PUBLIC_BUCKET,
        S3_UPLOAD_BUCKET: response.data.REACT_APP_S3_UPLOAD_BUCKET,
        S3_REGION: response.data.REACT_APP_S3_REGION,
      }
      
      // Validate that all required S3 config is present
      if (!config.S3_PRIVATE_BUCKET || !config.S3_PUBLIC_BUCKET || !config.S3_UPLOAD_BUCKET) {
        throw new Error("S3 configuration is incomplete. Please contact your administrator.")
      }
      
      setCmsConfig(config)
      setConfigLoading(false)
    } catch (error) {
      console.error("Failed to load CMS config:", error)
      setConfigError(error.message || "Failed to load S3 configuration. Please try again.")
      setConfigLoading(false)
    }
  }

  useEffect(() => {
    initProcess()
  }, [])

  useEffect(() => {
    const total = uploadList.length
    const validated = uploadList.filter((file) => file.validated).length
    const uploaded = uploadList.filter((file) => file.uploaded).length
    const uploading = uploadList.filter((file) => file.uploading).length
    setStats({ total, validated, uploaded, uploading })
  }, [uploadList])


  const startAllUploads = () => {
    document
      .querySelectorAll(".file-upload-btn.validated")
      .forEach((obj) => obj.click())
  }

  const removeFinishedUploads = () => {
    document
      .querySelectorAll(".file-remove-btn.uploadSucceeded")
      .forEach((obj) => obj.click())
  }

  const clearAllFiles = () => {
    setUploadList([])
  }

  if (configLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <CircularProgress size={40} />
        <Typography variant="body1" sx={{ marginLeft: 2 }}>
          Loading S3 configuration...
        </Typography>
      </Box>
    )
  }

  if (configError) {
    return (
      <Box sx={{ padding: 2 }}>
        <Alert 
          severity="error" 
          action={
            <Button color="inherit" size="small" onClick={initProcess}>
              Retry
            </Button>
          }
        >
          {configError}
        </Alert>
      </Box>
    )
  }

  return (
    <div>
      {selectionError && (
        <Alert severity="error" onClose={() => setSelectionError("")} sx={{ marginBottom: 2 }}>
          {selectionError}
        </Alert>
      )}
      <MediaDropZone onDrop={onDrop} />
      
      {uploadList.length > 0 && (
        <StyledMediaUploadControlPanel>
          <div className="upload-stats">
            <Typography variant="h6" component="span">
              {stats.total} Files Selected
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', marginTop: 1 }}>
              <Chip 
                label={`${stats.validated} Ready`} 
                color="success" 
                variant="outlined" 
                size="small"
              />
              <Chip 
                label={`${stats.uploaded} Uploaded`} 
                color="info" 
                variant="outlined" 
                size="small"
              />
              {stats.uploading > 0 && (
                <Chip 
                  label={`${stats.uploading} Uploading`} 
                  color="warning" 
                  variant="outlined" 
                  size="small"
                />
              )}
            </Box>
          </div>
          
          <div className="upload-actions">
            <Button
              variant="contained"
              endIcon={<SendIcon />}
              onClick={startAllUploads}
              disabled={stats.validated === 0}
              size="small"
            >
              Upload All Ready ({stats.validated})
            </Button>
            
            <Button
              variant="outlined"
              startIcon={<DeleteIcon />}
              onClick={removeFinishedUploads}
              disabled={stats.uploaded === 0}
              size="small"
            >
              Remove Finished ({stats.uploaded})
            </Button>
            
            <Button
              variant="outlined"
              color="error"
              startIcon={<DeleteIcon />}
              onClick={clearAllFiles}
              size="small"
            >
              Clear All
            </Button>
          </div>
        </StyledMediaUploadControlPanel>
      )}
      
      <StyledFileUploadCardZone>
        {uploadList.map((uploadItem, key) => (
          <MediaUploadCard
            key={`upload-file-${uploadItem.key || getRandomInt(1, 999999)}`}
            index={key}
            cmsConfig={cmsConfig}
            uploadFile={uploadItem}
            uploadList={uploadList}
            setUploadList={setUploadList}
            onSelect={(file) => {
              if (onSelect) onSelect(file);
              if (handleClose) handleClose();
            }}
          />
        ))}
      </StyledFileUploadCardZone>
      
      {uploadList.length === 0 && (
        <Box sx={{ textAlign: 'center', padding: 4, color: 'text.secondary' }}>
          <Typography variant="body1">
            No files selected. Drop files above or click to select files.
          </Typography>
        </Box>
      )}
    </div>
  )
}

export default MediaUpload
